/**
 * @file Avatar.tsx
 * @brief Circular avatar primitive — image with graceful fallback to
 *        gender-aware icon or text initials.
 *
 * @details
 * Used by surfaces that need to render a user / participant
 * (sessions list, transcript bubbles, owner strip, user picker, etc.)
 * with a consistent visual language. Mirrors the avatar treatment in
 * `FRONTEND/src/shared/components/VZUIKit/VZTranscriptBubble.tsx`:
 *   - When `src` is provided, render an `<img>` and `cover` it.
 *   - When `src` is missing but `gender` is `"male"` / `"female"`, show
 *     a Lucide silhouette icon coloured by the surface tone.
 *   - Otherwise fall back to up-to-2-letter initials with a high-
 *     contrast background.
 *
 * If the `<img>` fails to load (404, expired signed URL, etc.) we
 * silently fall through to the same initial / icon path so the table
 * never gets a broken image glyph.
 *
 * Sizes: sm/md/lg for tables and chrome; xl (56px) matches mobile
 * VZPicker LEADING_CIRCLE for flush picker-row accessories.
 *
 * @dependencies
 * - lucide-react: gender icons
 * - shared/theme/cn
 *
 * @references
 * - WEBPANEL/src/features/users/UsersPage.tsx (initial avatar pattern)
 * - FRONTEND/src/shared/components/VZUIKit/VZTranscriptBubble.tsx
 *
 * @author DES
 * @date 26-05-2026
 */

import { useEffect, useState } from "react";
import { User as UserIcon } from "lucide-react";
import { cn } from "@/shared/theme/cn";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------
type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
type AvatarTone = "brand" | "neutral";

export interface AvatarProps {
  src?: string | null;
  /** Fallback initials (max 2 characters). */
  initials?: string;
  /** Optional gender hint — drives the icon fallback when no image is
   *  present (mirrors mobile's gender-aware silhouette). */
  gender?: string | null;
  alt?: string;
  size?: AvatarSize;
  tone?: AvatarTone;
  className?: string;
}

// ----------------------------------------------------------------------
// Size + tone tokens — kept in plain objects so Tailwind's safelist
// picks them up at build time without dynamic class generation.
// ----------------------------------------------------------------------
const SIZE_CLASS: Record<AvatarSize, string> = {
  /** Inline inside a text chip (Meeting Intel ToDo assignee) — additive, no
   *  existing consumer changes. (03-08-2026) */
  xs: "h-5 w-5 text-[9px]",
  sm: "h-7 w-7 text-[10px]",
  md: "h-9 w-9 text-xs",
  lg: "h-11 w-11 text-sm",
  /** Mobile VZPicker LEADING_CIRCLE (56px) — flush picker rows. */
  xl: "h-14 w-14 text-base",
};

const TONE_CLASS: Record<AvatarTone, string> = {
  brand: "bg-brand-soft text-brand",
  neutral: "bg-secondary text-secondary-foreground",
};

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function Avatar({
  src,
  initials = "?",
  gender,
  alt = "",
  size = "md",
  tone = "brand",
  className,
}: AvatarProps) {
  // ----------------------------------------------------------------------
  // Local broken-src tracking — flip back to the fallback render when
  // the underlying <img> errors. We reset whenever `src` changes so a
  // refetch with a fresh signed URL clears the old failure.
  // ----------------------------------------------------------------------
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);

  const showImg = !!src && !broken;
  const g = gender?.toLowerCase();
  const showIcon = !showImg && (g === "male" || g === "female");

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold uppercase",
        SIZE_CLASS[size],
        showImg ? "bg-secondary" : TONE_CLASS[tone],
        className,
      )}
    >
      {showImg ? (
        <img
          src={src!}
          alt={alt}
          // Google account photos may refuse a request with another site's
          // Referer (DES 01-10-2026, a broken photo in the share menu).
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : showIcon ? (
        <UserIcon className="h-1/2 w-1/2 opacity-80" />
      ) : (
        <span className="leading-none">{initials}</span>
      )}
    </span>
  );
}
